/**
 * AuthorizationService - ABAC policy evaluation engine.
 *
 * Evaluates access requests against ABAC policies loaded from mock data.
 * Uses deny-overrides combining: any matching Deny policy wins over Permit.
 *
 * Designed as a drop-in replacement for a server-side PDP (Policy Decision Point)
 * when the backend is ready. Client-side enforcement is for UX only.
 */

import type {
  AbacPolicy,
  RoleDefinition,
  UiPolicyMapping,
  AuthorizationRequest,
  AuthorizationDecision,
  PolicySubjectMatch,
  PolicyResourceMatch,
  PolicyActionMatch,
  Action,
  ResourceType,
  SystemRole,
  PortfolioRole,
  PortfolioAssignment,
  AuthUser,
  EffectivePermission,
  AuthAuditEntry,
  NavPermission,
  OrganizationAccess,
  AccountType,
} from '@/types/authorization'
import { isAdminSystemRole } from '@/types/authorization'
import { supabase } from '@/lib/supabase'
import { isInternalSurface } from '@/config/productSurface'

// ---------------------------------------------------------------------------
// In-memory state
// ---------------------------------------------------------------------------

let policies: AbacPolicy[] = []
let roleDefinitions: RoleDefinition[] = []
let uiMappings: UiPolicyMapping[] = []
let portfolioAssignments: PortfolioAssignment[] = []
let authUsers: AuthUser[] = []
// ProjectTasks #78f Slice 2 — org tree state so descendant resolution
// works for `includeDescendants` access entries.
interface OrgNode { id: string; parentId: string | null; name: string }
let organizations: OrgNode[] = []
const auditLog: AuthAuditEntry[] = []
let initialized = false

// Demo accounts are blocked from these actions on production-class data.
// See [[feedback_table_standards]] / #78f gating-features-and-data decision.
const DEMO_BLOCKED_ACTIONS = new Set<Action>(['delete', 'approve', 'manage'])

// ---------------------------------------------------------------------------
// Navigation permission map
// ---------------------------------------------------------------------------

/** Defines what permissions each sidebar nav item requires */
export const NAV_PERMISSIONS: NavPermission[] = [
  // Portfolio context pages
  { key: 'dashboard', action: 'view', resourceType: 'Portfolio' },
  { key: 'budget-releases', action: 'view', resourceType: 'Budget' },
  { key: 'scenario-planning', action: 'view', resourceType: 'Scenario' },
  { key: 'resource-consumption', action: 'view', resourceType: 'Resource' },
  { key: 'projects', action: 'view', resourceType: 'Project' },
  { key: 'project-requests', action: 'view', resourceType: 'ProjectRequest' },
  { key: 'priorities', action: 'view', resourceType: 'Portfolio' },
  { key: 'workflows', action: 'view', resourceType: 'Workflow' },
  { key: 'analysis', action: 'view', resourceType: 'Analysis' },
  { key: 'risks', action: 'view', resourceType: 'Risk' },

  // Project context pages
  { key: 'project-overview', action: 'view', resourceType: 'Project' },
  { key: 'gantt', action: 'view', resourceType: 'Schedule' },
  { key: 'finance', action: 'view', resourceType: 'Baseline' },
  { key: 'forecasts', action: 'view', resourceType: 'Forecast' },
  { key: 'evm', action: 'view', resourceType: 'EVM' },
  { key: 'hierarchy', action: 'view', resourceType: 'WBS' },
  { key: 'project-risks', action: 'view', resourceType: 'Risk' },
  { key: 'project-resources', action: 'view', resourceType: 'Resource' },
  { key: 'reports', action: 'view', resourceType: 'Report' },
  { key: 'contracts', action: 'view', resourceType: 'Contract' },

  // Configuration context pages
  { key: 'config-users', action: 'manage', resourceType: 'UserAssignment' },
  { key: 'config-roles', action: 'manage', resourceType: 'RoleDefinition' },
  { key: 'config-policies', action: 'manage', resourceType: 'AbacPolicy' },
  { key: 'config-audit', action: 'view', resourceType: 'AuditLog' },
  { key: 'config-ui-mappings', action: 'manage', resourceType: 'AbacPolicy' },
]

// ---------------------------------------------------------------------------
// Role → editable resource mapping for PortfolioAnalyst
// ---------------------------------------------------------------------------

const ANALYST_EDITABLE_RESOURCES: ResourceType[] = [
  'Budget', 'BudgetData', 'Forecast', 'Analysis', 'Report', 'Scenario',
]

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

function isSupabaseMode(): boolean {
  return (import.meta.env.VITE_DATA_SOURCE as string) === 'supabase'
}

/** Convert snake_case DB row to camelCase */
function snakeToCamelRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row)) {
    if (k === 'created_at' || k === 'updated_at') continue
    out[k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())] = v
  }
  return out
}

/** For JSONB tables (id + data column), merge data into top-level */
function mergeJsonbRow(row: Record<string, unknown>): Record<string, unknown> {
  const { data: jsonData, created_at, updated_at, ...cols } = row
  const result: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(cols)) {
    result[k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())] = v
  }
  if (jsonData && typeof jsonData === 'object') {
    Object.assign(result, jsonData as Record<string, unknown>)
  }
  return result
}

async function initFromSupabase(): Promise<void> {
  // ProjectTasks #78f — also pull organizations + user_organization_access
  // so the User Management grid can resolve org names + accessible-org
  // counts per user without a second round-trip.
  const [policiesRes, rolesRes, mappingsRes, assignmentsRes, usersRes, orgsRes, orgAccessRes] = await Promise.all([
    supabase.from('abac_policies').select('*'),
    supabase.from('role_definitions').select('*'),
    supabase.from('ui_policy_mappings').select('*'),
    supabase.from('auth_portfolio_assignments').select('*'),
    supabase.from('auth_users_config').select('*'),
    supabase.from('organizations').select('id, data'),
    supabase.from('user_organization_access').select('*'),
  ])

  policies = (policiesRes.data ?? []).map(r => snakeToCamelRow(r as Record<string, unknown>) as unknown as AbacPolicy)
  roleDefinitions = (rolesRes.data ?? []).map(r => mergeJsonbRow(r as Record<string, unknown>) as unknown as RoleDefinition)
  uiMappings = (mappingsRes.data ?? []).map(r => mergeJsonbRow(r as Record<string, unknown>) as unknown as UiPolicyMapping)
  portfolioAssignments = (assignmentsRes.data ?? []).map(r => mergeJsonbRow(r as Record<string, unknown>) as unknown as PortfolioAssignment)

  // Resolve {organizationId → name} for the org-access enrichment below.
  // ProjectTasks #78f Slice 2 — also keep the parent_id chain in module
  // state so descendant resolution works for `includeDescendants` access
  // entries (see getAccessibleOrgIdsForUser).
  type OrgRow = { id: string; data: { name?: string; parentId?: string | null } | null }
  const orgRows = (orgsRes.data ?? []) as OrgRow[]
  organizations = orgRows.map(o => ({
    id: o.id,
    parentId: o.data?.parentId ?? null,
    name: o.data?.name ?? o.id,
  }))
  const orgNameById = new Map<string, string>(
    organizations.map(o => [o.id, o.name]),
  )

  // Build per-user org-access list. Keyed by user_id so we can splice
  // into each authUser row below.
  type OrgAccessRow = {
    id: string
    user_id: string
    organization_id: string
    access_level: 'view' | 'edit' | 'admin'
    include_descendants: boolean
    granted_by: string | null
    granted_at: string | null
    notes: string | null
  }
  const accessByUserId = new Map<string, OrganizationAccess[]>()
  for (const r of ((orgAccessRes.data ?? []) as OrgAccessRow[])) {
    const entry: OrganizationAccess = {
      organizationId: r.organization_id,
      organizationName: orgNameById.get(r.organization_id),
      accessLevel: r.access_level,
      includeDescendants: r.include_descendants,
      grantedBy: r.granted_by,
      grantedAt: r.granted_at,
      notes: r.notes,
    }
    const list = accessByUserId.get(r.user_id) ?? []
    list.push(entry)
    accessByUserId.set(r.user_id, list)
  }

  authUsers = (usersRes.data ?? []).map(r => {
    const camel = snakeToCamelRow(r as Record<string, unknown>) as unknown as AuthUser
    // Splice in org-access for this user. Falls back to empty array so
    // every AuthUser carries the field (no `undefined` access checks
    // elsewhere).
    return {
      ...camel,
      accountType: camel.accountType ?? 'production',
      primaryOrganizationId: camel.primaryOrganizationId ?? null,
      organizationAccess: accessByUserId.get(camel.id) ?? [],
    }
  })
}

async function initFromMock(): Promise<void> {
  const [policiesData, rolesData, mappingsData, assignmentsData, usersData] = await Promise.all([
    fetch('/mockdata/authorization/abac-policies.json').then(r => r.json()),
    fetch('/mockdata/authorization/role-definitions.json').then(r => r.json()),
    fetch('/mockdata/authorization/ui-policy-mappings.json').then(r => r.json()),
    fetch('/mockdata/authorization/portfolio-assignments.json').then(r => r.json()).catch(() => ({ assignments: [] })),
    fetch('/mockdata/authorization/auth-users.json').then(r => r.json()).catch(() => ({ users: [] })),
  ])

  policies = policiesData.policies ?? []
  roleDefinitions = rolesData.roles ?? []
  uiMappings = mappingsData.mappings ?? []
  portfolioAssignments = assignmentsData.assignments ?? []
  // ProjectTasks #78f — mock-mode AuthUsers default to production / no
  // org affiliation since the mock JSON predates the schema. UI guards
  // on undefined elsewhere, but seeding sensible defaults here keeps
  // the User Management grid columns from rendering "—" everywhere.
  authUsers = (usersData.users ?? []).map((u: AuthUser) => ({
    ...u,
    accountType: u.accountType ?? 'production',
    primaryOrganizationId: u.primaryOrganizationId ?? null,
    organizationAccess: u.organizationAccess ?? [],
  }))
}

/**
 * ProjectTasks #78f Slice 4 — flip the initialized flag so the next
 * call to initializeAuthorization() refetches everything from
 * Supabase. Used by admin mutations (User Management drawer) that
 * change auth state and need the in-memory cache to refresh.
 */
export function invalidateAuthCache(): void {
  initialized = false
}

export async function initializeAuthorization(): Promise<void> {
  if (initialized) return

  if (isSupabaseMode()) {
    await initFromSupabase()
  } else {
    await initFromMock()
  }

  // Seed the audit log with realistic historic decisions so the Config
  // Dashboard's "Recent Activity" panel isn't empty on first walk.
  // The audit log is in-memory only — these aren't persisted across reloads.
  // Live policy evaluations during the session push to the same list and
  // appear at the top (reversed in getAuthAuditLog).
  seedDemoAuditLog()

  initialized = true
}

function seedDemoAuditLog(): void {
  if (auditLog.length > 0) return
  const now = Date.now()
  const HOUR = 3_600_000
  const DAY = 24 * HOUR
  // Build entries chronologically (oldest first). getAuthAuditLog
  // reverses on return so the dashboard shows newest first.
  const entries: Array<Omit<AuthAuditEntry, 'id' | 'timestamp'> & { offset: number }> = [
    { userId: 'user_002', userName: 'Maria Santos',   action: 'approve', resourceType: 'Budget',         resourceId: 'rel_2026_q1_a321',  decision: 'Permit', matchedPolicies: ['policy_pm_approve_within_band'], reason: 'PM authority within $100K band — A321XLR FY26 Q1 release', offset: 7 * DAY },
    { userId: 'user_005', userName: 'Robert Kim',     action: 'approve', resourceType: 'Budget',         resourceId: 'bcr_2026_005',     decision: 'Permit', matchedPolicies: ['policy_vp_ops_unlimited'],     reason: 'VP Operations — unlimited BCR authority', offset: 6 * DAY + 4 * HOUR },
    { userId: 'user_003', userName: 'Thomas Park',    action: 'edit',    resourceType: 'Project',        resourceId: 'proj_787_etops',   decision: 'Permit', matchedPolicies: ['policy_chief_eng_edit'],       reason: 'Chief Engineer — edit authority on assigned program', offset: 5 * DAY + 2 * HOUR },
    { userId: 'user_001', userName: 'James Mitchell', action: 'create',  resourceType: 'Risk',           resourceId: 'rsk_a321_023',     decision: 'Permit', matchedPolicies: ['policy_pm_create_risk'],       reason: 'PM may file risks on assigned projects', offset: 4 * DAY + 6 * HOUR },
    { userId: 'user_004', userName: 'Linda Nakamura', action: 'approve', resourceType: 'Budget',         resourceId: 'rel_2026_rd_q2',   decision: 'Permit', matchedPolicies: ['policy_rd_pm_funding'],        reason: 'R&D PM — Funding Release within $500K cap', offset: 4 * DAY },
    { userId: 'user_002', userName: 'Maria Santos',   action: 'approve', resourceType: 'Task',           resourceId: 'ts_emp_003_w17',   decision: 'Permit', matchedPolicies: ['policy_supervisor_timesheet'], reason: 'Direct report — Thomas Park W17 timesheet submission', offset: 3 * DAY + 8 * HOUR },
    { userId: 'user_001', userName: 'James Mitchell', action: 'approve', resourceType: 'Budget',         resourceId: 'bcr_2026_011',     decision: 'Permit', matchedPolicies: ['policy_pm_bcr_band'],          reason: 'Project-scope BCR within $500K PM band', offset: 3 * DAY + 2 * HOUR },
    { userId: 'user_006', userName: 'Patricia Adams', action: 'approve', resourceType: 'Budget',         resourceId: 'po_facil_847',     decision: 'Permit', matchedPolicies: ['policy_director_po'],          reason: 'Director-level PO authority ($1M cap)', offset: 2 * DAY + 5 * HOUR },
    { userId: 'user_001', userName: 'James Mitchell', action: 'approve', resourceType: 'Budget',         resourceId: 'rel_2026_q2_a321', decision: 'Deny',   matchedPolicies: ['policy_pm_approve_within_band'], reason: 'Amount $325K exceeds James Mitchell PM band ($250K cap)', offset: 2 * DAY + 2 * HOUR },
    { userId: 'user_002', userName: 'Maria Santos',   action: 'create',  resourceType: 'Budget',         resourceId: 'bcr_draft_a321',   decision: 'Permit', matchedPolicies: ['policy_pm_create_bcr'],        reason: 'PM may draft change requests on owned program', offset: 1 * DAY + 7 * HOUR },
    { userId: 'user_005', userName: 'Robert Kim',     action: 'manage',  resourceType: 'Portfolio',      resourceId: 'port_gentex_corp', decision: 'Permit', matchedPolicies: ['policy_vp_portfolio_admin'],   reason: 'VP Ops — portfolio admin on Gentex Corporate', offset: 1 * DAY + 3 * HOUR },
    { userId: 'user_003', userName: 'Thomas Park',    action: 'approve', resourceType: 'Budget',         resourceId: 'rel_2026_q3_787',  decision: 'Deny',   matchedPolicies: [],                              reason: 'Chief Engineer has no approval authority on PO releases — escalate to PM', offset: 18 * HOUR },
    { userId: 'user_001', userName: 'James Mitchell', action: 'edit',    resourceType: 'Project',        resourceId: 'proj_787_etops',   decision: 'Permit', matchedPolicies: ['policy_pm_edit_owned'],        reason: 'PM owns the project — edit allowed', offset: 12 * HOUR },
    { userId: 'user_004', userName: 'Linda Nakamura', action: 'view',    resourceType: 'Forecast',       resourceId: 'fcst_rd_eac_2026', decision: 'Permit', matchedPolicies: ['policy_pm_view_owned'],        reason: 'PM view authority on R&D forecast', offset: 6 * HOUR },
    { userId: 'user_005', userName: 'Robert Kim',     action: 'approve', resourceType: 'Contract',       resourceId: 'sub_optics_004',   decision: 'Permit', matchedPolicies: ['policy_vp_subcontract'],       reason: 'VP Ops — Subcontract Award within $2M cap', offset: 2 * HOUR },
  ]
  let seq = 0
  for (const e of entries) {
    auditLog.push({
      ...e,
      id: `auth_audit_seed_${(seq++).toString(36).padStart(3, '0')}`,
      timestamp: new Date(now - e.offset).toISOString(),
    })
  }
}

// ---------------------------------------------------------------------------
// Core policy evaluation engine
// ---------------------------------------------------------------------------

/**
 * Evaluate an authorization request against all active policies.
 * Uses deny-overrides: if ANY matching Deny policy exists, access is denied.
 */
export function evaluate(request: AuthorizationRequest): AuthorizationDecision {
  // System role shortcuts. GlobalAdmin (platform master dev) and OrgAdmin
  // (customer top admin) both get full admin actions; they differ only in org
  // reach (GlobalAdmin all-org / OrgAdmin scoped by RLS in a fork), handled by
  // the org-scope layer (filterByAccessibleOrgs / scopeOrgId), not here.
  if (isAdminSystemRole(request.subject.systemRole)) {
    const isGlobal = request.subject.systemRole === 'GlobalAdmin'
    return {
      allowed: true,
      effect: 'Permit',
      matchedPolicies: [isGlobal ? 'SYSTEM_GLOBAL_ADMIN' : 'SYSTEM_ORG_ADMIN'],
      obligations: [],
      reason: isGlobal ? 'Global Administrator has full access' : 'Organization Administrator has full access within their org',
    }
  }

  if (request.subject.systemRole === 'GlobalViewer') {
    const allowed = request.action === 'view'
    return {
      allowed,
      effect: allowed ? 'Permit' : 'Deny',
      matchedPolicies: ['SYSTEM_GLOBAL_VIEWER'],
      obligations: [],
      reason: allowed
        ? 'Global Viewer has read-only access'
        : 'Global Viewer cannot perform write actions',
    }
  }

  if (request.subject.systemRole === 'ConfigAdmin') {
    const isConfigResource = isConfigurationResource(request.resource.type)
    if (isConfigResource) {
      return {
        allowed: true,
        effect: 'Permit',
        matchedPolicies: ['SYSTEM_CONFIG_ADMIN'],
        obligations: [],
        reason: 'Configuration Admin has full access to configuration resources',
      }
    }
    return {
      allowed: false,
      effect: 'Deny',
      matchedPolicies: ['SYSTEM_CONFIG_ADMIN'],
      obligations: [],
      reason: 'Configuration Admin can only access configuration resources',
    }
  }

  // Evaluate ABAC policies
  const activePolicies = policies
    .filter(p => p.active)
    .sort((a, b) => a.priority - b.priority)

  const matchedPermits: AbacPolicy[] = []
  const matchedDenies: AbacPolicy[] = []

  for (const policy of activePolicies) {
    if (matchesPolicy(policy, request)) {
      if (policy.effect === 'Deny') {
        matchedDenies.push(policy)
      } else {
        matchedPermits.push(policy)
      }
    }
  }

  // Deny-overrides: any deny wins
  if (matchedDenies.length > 0) {
    return {
      allowed: false,
      effect: 'Deny',
      matchedPolicies: matchedDenies.map(p => p.policyId),
      obligations: matchedDenies.flatMap(p => p.obligations),
      reason: `Denied by: ${matchedDenies.map(p => p.policyName).join(', ')}`,
    }
  }

  if (matchedPermits.length > 0) {
    return {
      allowed: true,
      effect: 'Permit',
      matchedPolicies: matchedPermits.map(p => p.policyId),
      obligations: matchedPermits.flatMap(p => p.obligations),
      reason: `Permitted by: ${matchedPermits.map(p => p.policyName).join(', ')}`,
    }
  }

  // Default deny if no policies match
  return {
    allowed: false,
    effect: 'Deny',
    matchedPolicies: [],
    obligations: [],
    reason: 'No matching policies found — default deny',
  }
}

// ---------------------------------------------------------------------------
// Policy matching
// ---------------------------------------------------------------------------

function matchesPolicy(policy: AbacPolicy, request: AuthorizationRequest): boolean {
  const { target } = policy

  // Match subjects
  if (target.subjects.length > 0 && !target.subjects.every(s => matchSubject(s, request))) {
    return false
  }

  // Match resources
  if (target.resources.length > 0 && !target.resources.every(r => matchResource(r, request))) {
    return false
  }

  // Match actions
  if (target.actions.length > 0 && !target.actions.some(a => matchAction(a, request))) {
    return false
  }

  // Match environment
  if (target.environment.length > 0 && !target.environment.every(e => matchEnvironment(e, request))) {
    return false
  }

  return true
}

function matchSubject(match: PolicySubjectMatch, request: AuthorizationRequest): boolean {
  const subjectValue = String(request.subject[match.attributeName] ?? '')
  return compareValues(subjectValue, match.attributeValue, match.operator)
}

function matchResource(match: PolicyResourceMatch, request: AuthorizationRequest): boolean {
  // Handle variable substitution (e.g., ${subject.assignedPortfolios})
  let matchValue = match.attributeValue
  if (matchValue.startsWith('${subject.')) {
    const subjectAttr = matchValue.slice(10, -1)
    const subjectVal = request.subject[subjectAttr]
    if (Array.isArray(subjectVal)) {
      // For array attributes (like assignedPortfolios), check if resource value is in the array
      const resourceValue = match.attributeName === 'resourceType'
        ? request.resource.type
        : String(request.resource[match.attributeName] ?? '')
      return subjectVal.includes(resourceValue)
    }
    matchValue = String(subjectVal ?? '')
  }

  const resourceValue = match.attributeName === 'resourceType'
    ? request.resource.type
    : String(request.resource[match.attributeName] ?? '')

  return compareValues(resourceValue, matchValue, match.operator)
}

function matchAction(match: PolicyActionMatch, request: AuthorizationRequest): boolean {
  return match.actionId === request.action
}

function matchEnvironment(match: PolicyResourceMatch, request: AuthorizationRequest): boolean {
  const envValue = String(request.environment?.[match.attributeName] ?? '')
  return compareValues(envValue, match.attributeValue, match.operator)
}

function compareValues(actual: string, expected: string, operator: string): boolean {
  switch (operator) {
    case 'equals': return actual === expected
    case 'not_equals': return actual !== expected
    case 'contains': return actual.includes(expected)
    case 'not_contains': return !actual.includes(expected)
    case 'in': return expected.split(',').map(s => s.trim()).includes(actual)
    case 'not_in': return !expected.split(',').map(s => s.trim()).includes(actual)
    case 'greater_than': return Number(actual) > Number(expected)
    case 'less_than': return Number(actual) < Number(expected)
    default: return false
  }
}

// ---------------------------------------------------------------------------
// Convenience helpers
// ---------------------------------------------------------------------------

function isConfigurationResource(type: ResourceType): boolean {
  return ['AbacPolicy', 'RoleDefinition', 'UserAssignment', 'AuditLog'].includes(type)
}

/**
 * Whether an admin role bypasses the org-scope filter entirely (#116 hardening).
 * - **GlobalAdmin** (internal all-org master dev): always sees every org in-app.
 * - **OrgAdmin** (customer top admin): sees all orgs ONLY on the internal/demo
 *   surface — so the shared demo (its admin personas are OrgAdmin post-`00389`)
 *   is unchanged. On a PRODUCT build (a real customer fork) OrgAdmin is
 *   org-scoped by this filter as defense-in-depth, so isolation doesn't rely
 *   solely on the fork RLS (which isn't applied on the shared demo).
 * Pure — `isInternal` is passed in so this is unit-testable without the surface.
 * See role_model_globaladmin_orgadmin.
 */
export function adminSeesAllOrgs(systemRole: string | null | undefined, isInternal: boolean): boolean {
  if (systemRole === 'GlobalAdmin') return true
  if (systemRole === 'OrgAdmin') return isInternal
  return false
}

/**
 * ProjectTasks #78f Slice 2b — filter helper used by cross-app data
 * queries (portfolio / project / resource lists) so production accounts
 * only see entities tied to organizations they can access. Demo accounts
 * see only demo-flagged rows. GlobalAdmin sees everything.
 *
 * The `getOrgId` extractor lets callers pull the org from whatever field
 * the record uses (organizationId on the camelCased model, or
 * organization_id on a raw row). Returns the unchanged list when the
 * user is GlobalAdmin or no access constraints apply.
 *
 * Usage:
 *   const visible = filterByAccessibleOrgs(projects, currentUser.id, p => p.organizationId)
 */
export function filterByAccessibleOrgs<T>(
  rows: T[],
  userId: string,
  getOrgId: (row: T) => string | null | undefined,
  getDataClass?: (row: T) => 'demo' | 'production' | null | undefined,
): T[] {
  const user = authUsers.find(u => u.id === userId)
  if (!user) return rows  // unknown user — don't filter, let the caller surface the auth error
  if (adminSeesAllOrgs(user.systemRole, isInternalSurface())) return rows

  const accessibleOrgs = getAccessibleOrgIdsForUser(userId)
  const accountType = user.accountType ?? 'production'

  return rows.filter(row => {
    // Data-class guard — production accounts hide demo rows; demo accounts
    // hide production rows.
    if (getDataClass) {
      const dc = getDataClass(row)
      if (dc === 'demo' && accountType === 'production') return false
      if (dc === 'production' && accountType === 'demo') return false
    }
    // Org-scope guard — if the row has an org tag, the user must have access.
    // Rows with no org tag are visible (legacy / unscoped data).
    const orgId = getOrgId(row)
    if (orgId && !accessibleOrgs.has(orgId)) return false
    return true
  })
}

/**
 * ProjectTasks #78f Slice 2 — return every org ID the user can access,
 * expanding `includeDescendants` entries through the parent_id tree.
 * Returns a Set so callers can do O(1) lookups in hot paths (cross-app
 * filtering).
 *
 * Always includes the user's `primaryOrganizationId` if set.
 */
export function getAccessibleOrgIdsForUser(userId: string): Set<string> {
  const out = new Set<string>()
  const user = authUsers.find(u => u.id === userId)
  if (!user) return out

  // Primary org always accessible.
  if (user.primaryOrganizationId) out.add(user.primaryOrganizationId)

  // Walk each access entry; if includeDescendants, add the org plus all
  // descendant orgs in the tree.
  for (const entry of user.organizationAccess ?? []) {
    out.add(entry.organizationId)
    if (entry.includeDescendants) {
      addDescendantOrgs(entry.organizationId, out)
    }
  }
  return out
}

function addDescendantOrgs(rootId: string, into: Set<string>): void {
  // Walk children iteratively to avoid recursion blow-out on weird trees.
  const stack = [rootId]
  while (stack.length > 0) {
    const id = stack.pop()
    if (!id) continue
    for (const o of organizations) {
      if (o.parentId === id && !into.has(o.id)) {
        into.add(o.id)
        stack.push(o.id)
      }
    }
  }
}


/**
 * Quick permission check — builds a request from the current user context
 * and evaluates against policies.
 */
export function checkPermission(
  userId: string,
  systemRole: SystemRole,
  portfolioRole: PortfolioRole | null,
  assignedPortfolios: string[],
  clearance: string,
  department: string,
  action: Action,
  resourceType: ResourceType,
  resourcePortfolioId?: string,
  // ProjectTasks #78f Slice 2 — optional org-scope + data-class params.
  // When omitted, the function looks them up from the loaded AuthUser
  // (zero-friction for legacy callers).
  resourceOrganizationId?: string,
  resourceDataClass?: 'demo' | 'production',
): AuthorizationDecision {
  const role = portfolioRole ?? systemRole
  // ProjectTasks #78f Slice 2 — pull account-type + accessible orgs from
  // the loaded AuthUser. GlobalAdmin still wins everything; the org-scope
  // + demo-account checks below are no-ops for that case.
  const user = authUsers.find(u => u.id === userId)
  const accountType: AccountType = user?.accountType ?? 'production'
  const accessibleOrgIds = isAdminSystemRole(systemRole)
    ? null  // admin tiers see every org IN-APP (null = no filter); OrgAdmin's
            // single-org scoping is enforced by fork RLS, not here.
    : getAccessibleOrgIdsForUser(userId)

  const request: AuthorizationRequest = {
    subject: {
      userId,
      role,
      systemRole,
      assignedPortfolios,
      clearance,
      department,
      accountType,
      primaryOrganizationId: user?.primaryOrganizationId ?? null,
      accessibleOrganizationIds: accessibleOrgIds ? Array.from(accessibleOrgIds) : undefined,
    },
    action,
    resource: {
      type: resourceType,
      portfolioId: resourcePortfolioId,
      organizationId: resourceOrganizationId,
      dataClass: resourceDataClass,
    },
  }

  // ProjectTasks #78f Slice 2c — Demo-account guard.
  // Demo accounts can VIEW + EDIT + CREATE for storyboarding purposes,
  // but block DELETE / APPROVE / MANAGE actions that would mutate
  // shared state. Lets a demo run through the app without breaking it.
  // GlobalAdmin override is intentional — Carey running as user_001 in
  // demo mode for a walkthrough still needs to demo approval flows.
  if (accountType === 'demo' && systemRole !== 'GlobalAdmin' && DEMO_BLOCKED_ACTIONS.has(action)) {
    return {
      allowed: false,
      effect: 'Deny',
      matchedPolicies: ['DEMO_ACCOUNT_GUARD'],
      obligations: [],
      reason: `Demo accounts cannot ${action} — use a production account for shared-state mutations`,
    }
  }

  // ProjectTasks #78f Slice 2b — Production accounts shouldn't see
  // demo-flagged resources by default (avoids storyboard data appearing
  // in real-customer-facing reports). GlobalAdmin override.
  if (accountType === 'production' && resourceDataClass === 'demo' && systemRole !== 'GlobalAdmin') {
    return {
      allowed: false,
      effect: 'Deny',
      matchedPolicies: ['PRODUCTION_DATA_CLASS_GUARD'],
      obligations: [],
      reason: 'Production account cannot access demo-flagged resource',
    }
  }

  // ProjectTasks #78f Slice 2a — Org-scope check.
  // When the resource carries an organizationId AND the user has a
  // bounded accessible-org set, require membership. GlobalAdmin
  // bypasses (accessibleOrgIds === null).
  if (accessibleOrgIds !== null && resourceOrganizationId) {
    if (!accessibleOrgIds.has(resourceOrganizationId)) {
      return {
        allowed: false,
        effect: 'Deny',
        matchedPolicies: ['ORG_SCOPE_CHECK'],
        obligations: [],
        reason: `User does not have access to organization ${resourceOrganizationId}`,
      }
    }
  }

  // For portfolio-scoped roles, check portfolio assignment
  if (systemRole === 'None' && portfolioRole) {
    if (resourcePortfolioId && !assignedPortfolios.includes(resourcePortfolioId)) {
      return {
        allowed: false,
        effect: 'Deny',
        matchedPolicies: ['PORTFOLIO_SCOPE_CHECK'],
        obligations: [],
        reason: `User not assigned to portfolio ${resourcePortfolioId}`,
      }
    }

    // PortfolioAnalyst can only edit certain resources
    if (portfolioRole === 'PortfolioAnalyst' && action !== 'view') {
      if (!ANALYST_EDITABLE_RESOURCES.includes(resourceType)) {
        return {
          allowed: false,
          effect: 'Deny',
          matchedPolicies: ['ANALYST_EDIT_RESTRICTION'],
          obligations: [],
          reason: `Portfolio Analyst cannot ${action} ${resourceType} resources`,
        }
      }
    }

    // Viewer can only view
    if (portfolioRole === 'Viewer' && action !== 'view') {
      return {
        allowed: false,
        effect: 'Deny',
        matchedPolicies: ['VIEWER_RESTRICTION'],
        obligations: [],
        reason: 'Viewer role has read-only access',
      }
    }
  }

  return evaluate(request)
}

/**
 * Check if a nav item should be visible for the given user.
 */
export function isNavItemAllowed(
  navKey: string,
  systemRole: SystemRole,
  portfolioRole: PortfolioRole | null,
  assignedPortfolios: string[],
  clearance: string,
): boolean {
  // Config pages — admin tiers (GlobalAdmin / OrgAdmin) and ConfigAdmin
  if (navKey.startsWith('config-')) {
    return isAdminSystemRole(systemRole) || systemRole === 'ConfigAdmin'
  }

  const perm = NAV_PERMISSIONS.find(p => p.key === navKey)
  if (!perm) return true // No permission defined — allow by default

  const decision = checkPermission(
    '', systemRole, portfolioRole, assignedPortfolios, clearance, '',
    perm.action, perm.resourceType,
  )
  return decision.allowed
}

/**
 * Check if a UI component should be visible/enabled based on UI policy mappings.
 */
export function getUiComponentAccess(
  componentId: string,
  systemRole: SystemRole,
  portfolioRole: PortfolioRole | null,
  assignedPortfolios: string[],
  clearance: string,
): { visible: boolean; enabled: boolean } {
  const mapping = uiMappings.find(m => m.componentId === componentId)
  if (!mapping) return { visible: true, enabled: true }

  const decision = checkPermission(
    '', systemRole, portfolioRole, assignedPortfolios, clearance, '',
    mapping.requiredAction, mapping.resourceType,
  )

  return {
    visible: decision.allowed || mapping.defaultVisible,
    enabled: decision.allowed,
  }
}

// ---------------------------------------------------------------------------
// Effective permissions preview
// ---------------------------------------------------------------------------

export type { EffectivePermission }


/**
 * Calculate effective permissions for a user across all resource types.
 */
export function getEffectivePermissions(
  systemRole: SystemRole,
  portfolioRole: PortfolioRole | null,
  assignedPortfolios: string[],
  clearance: string,
  department: string,
  portfolioId?: string,
): EffectivePermission[] {
  const resourceTypes: ResourceType[] = [
    'Portfolio', 'Project', 'Risk', 'WBS', 'Task', 'Budget',
    'Contract', 'EVM', 'Forecast', 'Resource', 'Workflow',
    'Report', 'Analysis', 'Scenario', 'ProjectRequest',
    'AbacPolicy', 'RoleDefinition', 'UserAssignment', 'AuditLog',
  ]

  const actions: Action[] = ['view', 'edit', 'create', 'delete', 'approve', 'manage']

  return resourceTypes.map(resourceType => ({
    resourceType,
    actions: actions.map(action => {
      const decision = checkPermission(
        '', systemRole, portfolioRole, assignedPortfolios,
        clearance, department, action, resourceType, portfolioId,
      )
      return { action, allowed: decision.allowed, reason: decision.reason }
    }),
  }))
}

// ---------------------------------------------------------------------------
// Audit logging
// ---------------------------------------------------------------------------

export function logAuthDecision(entry: Omit<AuthAuditEntry, 'id' | 'timestamp'>): void {
  auditLog.push({
    ...entry,
    id: `auth_audit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
  })
}

export function getAuthAuditLog(): AuthAuditEntry[] {
  return [...auditLog].reverse()
}

// ---------------------------------------------------------------------------
// Data accessors
// ---------------------------------------------------------------------------

export function getPolicies(): AbacPolicy[] { return policies }
export function getRoleDefinitions(): RoleDefinition[] { return roleDefinitions }
export function getUiMappings(): UiPolicyMapping[] { return uiMappings }
export function getPortfolioAssignments(userId?: string): PortfolioAssignment[] {
  if (userId) return portfolioAssignments.filter(a => a.userId === userId)
  return portfolioAssignments
}
export function getAuthUsers(): AuthUser[] { return authUsers }
export function getAuthUserById(id: string): AuthUser | undefined {
  return authUsers.find(u => u.id === id)
}

/** Convert an AbacPolicy to its abac_policies row shape (camelCase → snake_case) */
function policyToRow(p: AbacPolicy): Record<string, unknown> {
  return {
    id: p.policyId,
    policy_name: p.policyName,
    description: p.description,
    effect: p.effect,
    priority: p.priority,
    active: p.active,
    target: p.target,
    conditions: p.conditions,
    obligations: p.obligations,
    updated_at: new Date().toISOString(),
  }
}

/** Fire-and-forget persistence helper (logs errors but doesn't block UI) */
function persistPolicy(p: AbacPolicy) {
  if (!isSupabaseMode()) return
  supabase.from('abac_policies').upsert(policyToRow(p)).then(({ error }) => {
    if (error) console.warn('[auth] failed to persist policy', p.policyId, error.message)
  })
}

/** Update a policy (for config UI) — persists to Supabase when in supabase mode */
export function updatePolicy(updated: AbacPolicy): void {
  const idx = policies.findIndex(p => p.policyId === updated.policyId)
  if (idx >= 0) policies[idx] = updated
  else policies.push(updated)
  persistPolicy(updated)
}

/** Toggle policy active state — persists to Supabase when in supabase mode */
export function togglePolicyActive(policyId: string): void {
  const policy = policies.find(p => p.policyId === policyId)
  if (policy) {
    policy.active = !policy.active
    persistPolicy(policy)
  }
}

/** Delete a policy (used by the visual builder) — persists when in supabase mode */
export function deletePolicy(policyId: string): void {
  policies = policies.filter(p => p.policyId !== policyId)
  if (isSupabaseMode()) {
    supabase.from('abac_policies').delete().eq('id', policyId).then(({ error }) => {
      if (error) console.warn('[auth] failed to delete policy', policyId, error.message)
    })
  }
}

/** Add a portfolio assignment */
export function addPortfolioAssignment(assignment: PortfolioAssignment): void {
  portfolioAssignments.push(assignment)
}

/** Remove a portfolio assignment */
export function removePortfolioAssignment(userId: string, portfolioId: string): void {
  portfolioAssignments = portfolioAssignments.filter(
    a => !(a.userId === userId && a.portfolioId === portfolioId)
  )
}
